// Helpers compartidos del Reporte Comercial Dinámico.

export const fmtNum = (v) => (v === null || v === undefined || v === "" ? "" :
  Number(v).toLocaleString("es-HN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
export const fmtInt = (v) => (v === null || v === undefined || v === "" ? "" : Number(v).toLocaleString("es-HN"));
export const fmtMoney = (v, moneda = "L") => (v === null || v === undefined ? "—" : `${moneda === "$" ? "$" : "L"} ${fmtNum(v)}`);
export const fmtPct = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  return `${n > 0 ? "+" : ""}${n.toFixed(1)}%`;
};

export const COMP_COLORS = {
  crecimiento: "text-emerald-400 bg-emerald-500/10",
  caida: "text-red-400 bg-red-500/10",
  sin_compra: "text-amber-400 bg-amber-500/10",
  nuevo: "text-sky-400 bg-sky-500/10",
  reactivado: "text-violet-400 bg-violet-500/10",
  estable: "text-text-muted bg-white/5",
  "n/d": "text-text-muted bg-white/5",
};
export const COMP_LABELS = {
  crecimiento: "Crecimiento", caida: "Caída", sin_compra: "Sin compra",
  nuevo: "Nuevo", reactivado: "Reactivado", estable: "Estable", "n/d": "—",
};
export const ALERTA_LABELS = {
  mas_und_menos_venta: "▲ Und ▼ Venta",
  menos_und_mas_venta: "▼ Und ▲ Venta",
};

export const varColor = (v) => (v > 0 ? "text-emerald-400" : v < 0 ? "text-red-400" : "text-text-muted");

export const unitsText = (obj) => Object.entries(obj || {})
  .map(([u, v]) => `${fmtNum(v)} ${u}`).join(" · ");

/** Formatea una celda según el tipo de columna del backend (s|n|m|i|p). */
export function fmtCell(v, t) {
  if (t === "m") return fmtMoney(v);
  if (t === "n") return fmtNum(v);
  if (t === "i") return fmtInt(v);
  if (t === "p") return fmtPct(v);
  return v === null || v === undefined || v === "" ? "—" : String(v);
}

/** Mismo criterio que el backend (_view_sort): números primero, luego texto. */
export function sortRows(rows, key, dir) {
  if (!key) return rows;
  const mul = dir === "asc" ? 1 : -1;
  const nums = rows.filter((r) => typeof r[key] === "number");
  const others = rows.filter((r) => typeof r[key] !== "number");
  nums.sort((a, b) => (a[key] - b[key]) * mul);
  others.sort((a, b) => String(a[key] ?? "").toLowerCase().localeCompare(String(b[key] ?? "").toLowerCase()) * mul);
  return nums.length ? [...nums, ...others] : others;
}

/** Mismo criterio que el backend (_search_rows). */
export function searchRows(rows, term) {
  const s = (term || "").trim().toLowerCase();
  if (!s) return rows;
  return rows.filter((r) => String(r.grupo || "").toLowerCase().includes(s)
    || (r.sub || []).some((x) => String(x.grupo || "").toLowerCase().includes(s)));
}

const r2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
const pct = (a, b) => (b ? r2(((a - b) / Math.abs(b)) * 100) : (a ? 100 : 0));

/** TOTAL GENERAL de las filas visibles (espejo de _totals del backend). */
export function computeTotal(rows, periodos) {
  const t = { grupo: "TOTAL GENERAL", facturas: rows.reduce((a, r) => a + (r.facturas || 0), 0) };
  periodos.forEach((p) => {
    const i = p.i;
    t[`unidades_${i}`] = r2(rows.reduce((a, r) => a + (r[`unidades_${i}`] || 0), 0));
    t[`monto_${i}`] = r2(rows.reduce((a, r) => a + (r[`monto_${i}`] || 0), 0));
    const units = {};
    rows.forEach((r) => Object.entries(r[`und_x_unidad_${i}`] || {}).forEach(([u, v]) => {
      units[u] = r2((units[u] || 0) + v);
    }));
    t[`und_x_unidad_${i}`] = units;
    const u = t[`unidades_${i}`];
    t[`pprom_${i}`] = u > 0 && Object.keys(units).length <= 1 ? r2(t[`monto_${i}`] / u) : null;
    if (i >= 1) {
      const sfx = i === 1 ? "" : `_${i}`;
      t[`var_unidades${sfx}`] = r2(t.unidades_0 - u);
      t[`var_unidades_pct${sfx}`] = pct(t.unidades_0, u);
      t[`var_monto${sfx}`] = r2(t.monto_0 - t[`monto_${i}`]);
      t[`var_monto_pct${sfx}`] = pct(t.monto_0, t[`monto_${i}`]);
    }
  });
  if (rows.some((r) => "costo_0" in r)) {
    t.costo_0 = r2(rows.reduce((a, r) => a + (r.costo_0 || 0), 0));
    t.utilidad_0 = r2(t.monto_0 - t.costo_0);
    t.margen_0 = t.monto_0 ? r2((t.utilidad_0 / t.monto_0) * 100) : 0;
  }
  t.unidades_mixtas = periodos.some((p) => Object.keys(t[`und_x_unidad_${p.i}`] || {}).length > 1);
  return t;
}

/** Cadena de drill-down por dimensión ancla. "factura" = hoja. */
export function drillNext(anchorDim, depth) {
  const chains = {
    linea: ["producto", "cliente", "factura"],
    categoria: ["producto", "cliente", "factura"],
    producto: ["cliente", "factura"],
    cliente: ["producto", "factura"],
    rtc: ["cliente", "producto", "factura"],
    region: ["rtc", "cliente", "producto", "factura"],
    proyecto: ["cliente", "producto", "factura"],
    mes: ["cliente", "producto", "factura"],
    semana: ["cliente", "producto", "factura"],
    anio: ["mes", "cliente", "producto", "factura"],
    presentacion: ["producto", "cliente", "factura"],
  };
  const chain = chains[anchorDim] || ["cliente", "producto", "factura"];
  return chain[depth];
}

export const DIM_LABEL = {
  cliente: "Cliente", producto: "Producto", linea: "Línea", categoria: "Categoría", rtc: "RTC",
  region: "Región", proyecto: "Proyecto", presentacion: "Presentación", proveedor: "Proveedor",
  subcategoria: "Subcategoría", nivel_precio: "Nivel de precio", canal: "Canal",
  semana: "Semana", mes: "Mes", anio: "Año", factura: "Facturas",
};

/** Query string a partir de un objeto de filtros (arrays → csv). */
export function buildQS(f) {
  const p = new URLSearchParams();
  Object.entries(f).forEach(([k, v]) => {
    if (k === "fijar") { if (v && v.length) p.set(k, JSON.stringify(v)); return; }
    if (Array.isArray(v)) { if (v.length) p.set(k, v.join(",")); }
    else if (typeof v === "boolean") { if (v) p.set(k, "true"); }
    else if (v !== "" && v !== null && v !== undefined && v !== 0) p.set(k, String(v));
    else if (k === "comparar_n" && Number(v) === 0) p.set(k, "0");
  });
  return p.toString();
}
